import { Dayjs } from 'dayjs';
import {
  AnalysisContext,
  AnalysisMetadataInput,
  AnalysisUseCase,
  Status,
} from 'generated/graphql';
import { WhereClause } from 'v2/core//services/queryBuilder/whereClause';
import { AggregatePeriod, GroupBy, TimeRange } from 'v2/core/entities';
import { SortBy } from 'v2/core/entities/feedback/sortBy';
import { Scope } from 'v2/core/entities/scope';
import { Timezone, User } from 'v2/core/entities/user';
import { UUID } from 'v2/core/entities/uuid';
import { ReportSchedule } from 'v2/core/repository/edge-server/analysis/types';
import { FieldData } from 'v2/core/services/quickFilters/useQuickFilters';
import { NotebookView } from 'v2/views/layouts/notebook/types';

export interface Analysis {
  id: UUID;
  name: string;
  query: AnalysisQuery;
  widgets: unknown[];
  feedback: FeedbackQueryContainer | undefined;
  createdBy: Pick<User, 'id' | 'firstName' | 'lastName'>;
  createdAt: Dayjs;
  updatedAt: Dayjs;
  scope: Scope;
  viewToken: UUID;
  editToken?: UUID;
  originViewToken?: UUID;
  status: Status;
  metadata?: string;
  useCase?: AnalysisUseCase;
  metadataV2?: AnalysisMetadataInput;
  context?: AnalysisContext;
  title?: string;
  description?: string;

  // metadata values
  queryString: string; // TODO: deprecate this, we can derive from queryTree
  quickFilters: FieldData;
  qbQuery: AnalysisQuery;
  selectedView: NotebookView;
  reportSchedule?: ReportSchedule;
  isReportingDisabled: boolean;
  searchQuery: string;
}

export enum QueryType {
  Records = 'records',
  Breakdown = 'breakdown',
  TimeSeries = 'time-series',
}

export interface AnalysisQuery {
  whereClause: WhereClause;
  dateRange: TimeRange;
  aggregatePeriod: AggregatePeriod;
  timezone: Timezone;
  taxonomyID: UUID;
}

export type AnalysisQueryContainer = Pick<
  AnalysisQuery,
  'whereClause' | 'dateRange' | 'aggregatePeriod'
>;

export interface RecordsQuery
  extends Omit<AnalysisQuery, 'aggregatePeriod' | 'timezone'> {
  type: QueryType.Records;
  sortBy: SortBy;
}

export interface BreakdownQuery
  extends Omit<AnalysisQuery, 'aggregatePeriod' | 'timezone'> {
  type: QueryType.Breakdown;
  groupBy: GroupBy;
}

export enum StatMetricFunction {
  SCORE = 'score',
  FEEDBACK = 'feedback',
}

export interface TimeSeriesQuery extends AnalysisQuery {
  type: QueryType.TimeSeries;
  groupBy: GroupBy;
  aggregateFunction?: StatMetricFunction;
}

export type Query = RecordsQuery | BreakdownQuery | TimeSeriesQuery;

export interface FeedbackQueryContainer {
  query: RecordsQuery;
}

export enum VisualisationType {
  Bar = 'bar',
  Pie = 'pie',
  TimeSeries = 'time-series',
  StackedBar = 'stacked-bar',
  SegregatedBarSentiment = 'segregated-bar-sentiment',
  StackedArea = 'stacked-area',
}

export enum AggregateType {
  Breakdown = 'breakdown',
  TimeSeries = 'time-series',
}
